/**
 * Application route table (React Router v6).
 * - Public routes: /login, /register.
 * - Protected routes are wrapped in <ProtectedRoute> (redirects to /login when
 *   unauthenticated) and rendered inside the <AppLayout> shell (Sidebar + Header
 *   + Footer + <Outlet/>).
 * Any unknown path falls back to the dashboard.
 */
import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import AppLayout from '@/components/Layout/AppLayout';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import FacebookAccounts from '@/pages/FacebookAccounts';
import AdAccounts from '@/pages/AdAccounts';
import Campaigns from '@/pages/Campaigns';
import Rules from '@/pages/Rules';
import Logs from '@/pages/Logs';
import Settings from '@/pages/Settings';

/** Top-level route configuration for the SPA. */
export default function App(): ReactElement {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Protected — guarded, then rendered inside the app shell. */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/facebook-accounts" element={<FacebookAccounts />} />
          <Route path="/ad-accounts" element={<AdAccounts />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/logs" element={<Logs />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
